import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Link2,
  Receipt,
  Wallet,
  UserCog,
  LogOut,
  Sun,
  Moon,
} from "lucide-react";
import { useAffAuth } from "./store";
import { useAffTheme, useAffThemeInit } from "./theme";
import { useT, useAffLangInit } from "./i18n";
import LangPicker from "./LangPicker";

const NAV = [
  { to: "/affiliate", end: true, key: "nav.dashboard", icon: LayoutDashboard, testid: "aff-nav-dashboard" },
  { to: "/affiliate/codes", key: "nav.codes", icon: Link2, testid: "aff-nav-codes" },
  { to: "/affiliate/transactions", key: "nav.transactions", icon: Receipt, testid: "aff-nav-transactions" },
  { to: "/affiliate/payouts", key: "nav.payouts", icon: Wallet, testid: "aff-nav-payouts" },
  { to: "/affiliate/profile", key: "nav.profile", icon: UserCog, testid: "aff-nav-profile" },
];

export default function AffiliateLayout() {
  const { partner, logout } = useAffAuth();
  const { theme, toggle: toggleTheme } = useAffTheme();
  const navigate = useNavigate();
  const t = useT();
  useAffThemeInit();
  useAffLangInit();
  useAffThemeInit();

  const onLogout = async () => {
    await logout();
    navigate("/affiliate/login");
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      <aside
        className="w-60 border-r border-border flex flex-col"
        data-testid="aff-sidebar"
      >
        <div className="px-5 py-5 border-b border-border">
          <div className="font-display text-xl font-medium tracking-tight">
            Clipra
          </div>
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground mt-0.5">
            {t("common.partners_subtitle")}
          </div>
        </div>
        <nav className="flex-1 py-3">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-2.5 text-sm transition-colors border-l-2 ${
                  isActive
                    ? "border-foreground text-foreground bg-secondary/40"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:bg-secondary/20"
                }`
              }
            >
              <item.icon className="w-4 h-4" />
              <span>{t(item.key)}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <button
            onClick={onLogout}
            data-testid="aff-logout-btn"
            className="w-full flex items-center gap-3 px-2 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span>{t("common.sign_out")}</span>
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-border flex items-center justify-between px-6 sticky top-0 bg-background/80 backdrop-blur-md z-10">
          <div className="flex items-center gap-3 text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">
            <span>{t("header.partner_cabinet")}</span>
            <span className="hidden sm:inline">·</span>
            <span className="hidden sm:inline">{new Date().toUTCString().slice(5, 22)} UTC</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <LangPicker compact />
            <button
              onClick={toggleTheme}
              data-testid="aff-theme-toggle"
              aria-label="Toggle theme"
              title={t("header.theme_toggle_to", { target: theme === "dark" ? "light" : "dark" })}
              className="w-7 h-7 inline-flex items-center justify-center border border-border text-muted-foreground hover:text-foreground hover:border-foreground/40 transition-colors"
            >
              {theme === "dark" ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
            </button>
            <span
              data-testid="aff-pct-badge"
              className="inline-flex items-center px-2 py-0.5 border border-emerald-500/40 text-emerald-400 text-[10px] font-mono uppercase tracking-[0.2em]"
              title={t("header.revenue_share_title")}
            >
              {partner?.commission_pct ?? 20}%
            </span>
            <span className="hidden md:inline text-muted-foreground font-mono text-xs">
              {partner?.email}
            </span>
          </div>
        </header>

        <main className="flex-1 p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
